import { createApp } from 'vue';
import App from './App.vue';
import cookies from './plugins/cookies';
import i18n from './i18n';
import router from './router';
import { setLocale } from './utils';
import store from './store';
import { FocusTrap } from 'focus-trap-vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import vSelect from 'vue-select';
import VueCookies from 'vue-cookies';
import { Vue3Mq } from 'vue3-mq';
import VueSmoothScroll from 'v-smooth-scroll';
import 'vue-select/dist/vue-select.css';
import { faArchive } from '@fortawesome/free-solid-svg-icons/faArchive';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faBullhorn } from '@fortawesome/free-solid-svg-icons/faBullhorn';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faWifi } from '@fortawesome/free-solid-svg-icons/faWifi';
import EventBus from '@/event-bus';
import FloatingVue from 'floating-vue';
import { resetAuthFailCount } from '@/services/auth.ts'

const Columns = () => import('@/views/hub/components/layout/Columns.vue');
const Rows = () => import('@/views/hub/components/layout/Rows.vue');
const Tabs = () => import('@/views/hub/components/layout/Tabs.vue');
const AUTH_ERROR_PATH = '/auth_error'
const SIGN_IN_PATH = '/sign_in'
const SIGNED_OUT_PATH = '/signed_out'
const PUBLIC_PATHS = [AUTH_ERROR_PATH, SIGN_IN_PATH, SIGNED_OUT_PATH]

library.add(faArchive);
library.add(faArrowUp);
library.add(faBars);
library.add(faBullhorn);
library.add(faChevronDown);
library.add(faChevronLeft);
library.add(faChevronRight);
library.add(faChevronUp);
library.add(faGear);
library.add(faInfoCircle);
library.add(faSearch);
library.add(faTimes);
library.add(faTrash);
library.add(faWifi);

(async () => {
    let authAttempt = false;
    let setupError;
    let config;
    try {
        if (!PUBLIC_PATHS.includes(window.location.pathname)) {
            localStorage.removeItem('base-url');
            localStorage.removeItem('auth-fail-count');
            config = await store.dispatch('fetchConfig');
            resetAuthFailCount()
            const cookies = VueCookies;
            if (!cookies.get('timezone')) {
                cookies.set('timezone', config.time_zone_name);
            }
        }

        window.addEventListener('offline', () => store.dispatch('setOffline', true));
        window.addEventListener('online', () => {
            store.dispatch('setOffline', false);
            EventBus.$emit('data-refresh');
        });

        const { userAgent, platform } = window.navigator;
        const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        const isMobileDevice = (/Android/.test(userAgent) || iosPlatforms.indexOf(platform) !== -1);

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && isMobileDevice) {
                EventBus.$emit('data-refresh');
            }
        });
    } catch (error) {
        console.log("Exception in main: ", error)
        if (String(error) === 'Error: unable-to-authorize') {
            await store.dispatch('setAuthFail');
        } else if (String(error) === 'Error: authorization-required') {
            authAttempt = true;
        } else if (!error.response) {
            setupError = error;
        } else if (error.response?.status === 404) {
            await store.dispatch('set404');
        } else if (error.response?.status === 401) {
            authAttempt = true;
        } else {
            setupError = error;
        }
    }
    if (!authAttempt) {
        const app = createApp(App);
        if (config?.sentry) {
            const Sentry = await import('@sentry/vue');
            Sentry.init({
                app,
                denyUrls: [
                    '127.0.0.1',
                    'localhost',
                    /local-.*\.statushub-dev\.com/
                ],
                dsn: config.sentry.dsn,
                release: import.meta.env.VITE_APP_VERSION
            });
            if (setupError !== undefined) {
                Sentry.captureException(setupError);
            }
        }
        if (import.meta.env.NODE_ENV !== 'test') {
            app.use(router);
        }
        app
            .use(cookies)
            .use(FloatingVue, {
                container: '#floating-root'
            })
            .use(i18n)
            .use(store)
            .use(Vue3Mq)
            .use(VueSmoothScroll)
            .component('Columns', Columns)
            .component('Rows', Rows)
            .component('Tabs', Tabs);
        if (config?.pusher?.app_key) {
            const pusher = (await import('./plugins/pusher')).default;
            app.use(pusher, {
                appKey: config.pusher.app_key,
                cluster: config.pusher.cluster,
                event: `status_page_${config.subdomain}`,
                channel: `status_page_${config.subdomain}`,
                router
            });
        }
        app.component('FocusTrap', FocusTrap)
            .component('font-awesome-icon', FontAwesomeIcon)
            .component('v-select', vSelect);
        if ((config?.analytics || []).length) {
            const loadAnalyticsScripts =
                (await import('@/services/analytics')).default;
            loadAnalyticsScripts(app, config.analytics);
        }
        if (import.meta.env.DEV && import.meta.env.VITE_ACCENTED === 'true') {
            const { accented } = await import('accented');
            accented();
        }

        try {
            app.mount('#app');
        } catch (e) {
            console.log(e);
        }
    }
})();
